import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { useCopyQuote, useGetQuote, useGetQuoteCustomers } from "../../api/useQuotes";
import Loader from "../../components/Loader/Loader";
import { APP_ROUTES } from "../../constants/appRoutes";
import { QuoteCopyTarget } from "../../shared/types";
import { resolveDynamicUrl } from "../../shared/utils";
import "./CopyQuotePage.scss";
import { quoteListErrorMessage } from "../QuoteListPage/quoteListUtils";

type TargetMode = "same" | "customer" | "prospect";

const CopyQuotePage = () => {
  const { quoteId = "" } = useParams();
  const navigate = useNavigate();
  const source = useGetQuote(quoteId);
  const organizations = useGetQuoteCustomers();
  const copy = useCopyQuote(quoteId);
  const [mode, setMode] = useState<TargetMode>("same");
  const [customerId, setCustomerId] = useState("");
  const [prospectName, setProspectName] = useState("");
  const [discounts, setDiscounts] = useState({ monthly: "0", installment: "0", annual: "0" });

  useEffect(() => {
    if (source.data?.quote_type !== "prospect") return;
    setProspectName(source.data.prospect_name || source.data.display_name);
    setDiscounts({
      monthly: String(source.data.prospect_monthly_discount ?? 0),
      installment: String(source.data.prospect_installment_discount ?? 0),
      annual: String(source.data.prospect_annual_discount ?? 0),
    });
  }, [source.data]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    let target: QuoteCopyTarget = {};
    if (mode === "customer") target = { quote_type: "customer", customer_id: Number(customerId) };
    if (mode === "prospect") target = {
      quote_type: "prospect", prospect_name: prospectName.trim(), prospect_email: null,
      prospect_monthly_discount: Number(discounts.monthly), prospect_installment_discount: Number(discounts.installment),
      prospect_annual_discount: Number(discounts.annual),
    };
    // Opens the new copy itself, not the list (same reasoning as CreateQuotePage's save) - `replace`
    // keeps Back from returning to this copy form and copying the quote a second time.
    copy.mutate(target, { onSuccess: copied => navigate(resolveDynamicUrl(APP_ROUTES.QUOTE_DETAIL, { quoteId: String(copied.quote_id) }), { replace: true }) });
  };

  const prospectValid = prospectName.trim() && Object.values(discounts).every(value => value !== "" && Number(value) >= 0 && Number(value) <= 100);
  const canSubmit = mode === "same" || (mode === "customer" ? !!customerId : !!prospectValid);
  if (source.isLoading) return <Loader />;
  if (source.isError || !source.data) return <Alert severity="error">Quote could not be loaded.</Alert>;

  return (
    <Box className="copy-quote-page">
      <Typography variant="h4">Copy CSP-CP-{source.data.quote_id}</Typography>
      <Typography color="textSecondary">Create a new draft with the same line items, repriced for the selected recipient.</Typography>
      <Box component="form" onSubmit={submit} className="copy-quote-form">
        <FormControl fullWidth>
          <InputLabel id="copy-target-label">Quote recipient</InputLabel>
          <Select className="quick-quote-select" labelId="copy-target-label" label="Quote recipient" value={mode} onChange={event => setMode(event.target.value as TargetMode)}>
            <MenuItem value="same">Same recipient ({source.data.display_name})</MenuItem>
            <MenuItem value="customer">Different customer</MenuItem>
            <MenuItem value="prospect">Different prospect</MenuItem>
          </Select>
        </FormControl>
        {mode === "customer" && (
          <FormControl fullWidth required>
            <InputLabel id="copy-customer-label">Customer</InputLabel>
            <Select className="quick-quote-select" labelId="copy-customer-label" label="Customer" value={customerId} onChange={event => setCustomerId(String(event.target.value))}>
              {(organizations.data || []).filter(item => Boolean(item.is_active)).map(item => <MenuItem key={item.id} value={item.id}>{item.display_name}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {mode === "prospect" && <>
          <TextField required label="Prospect name" value={prospectName} inputProps={{ maxLength: 200 }} onChange={event => setProspectName(event.target.value)} />
          <Box className="copy-discounts">
            {(["monthly", "installment", "annual"] as const).map((term, index) => <TextField key={term} required type="number" label={`${["P1M", "P1YM", "P1YA"][index]} discount %`} value={discounts[term]} inputProps={{ min: 0, max: 100, step: 0.01 }} onChange={event => setDiscounts({ ...discounts, [term]: event.target.value })} />)}
          </Box>
        </>}
        {copy.isError && <Alert severity="error">The quote could not be copied. {quoteListErrorMessage(copy.error, process.env.REACT_APP_ADMIN_API_BASE)}</Alert>}
        <Box className="copy-quote-actions">
          <Tooltip enterDelay={1800} title="Cancel without copying the quote."><Button color="inherit" variant="outlined" onClick={() => navigate(APP_ROUTES.QUOTES)}>Cancel</Button></Tooltip>
          <Tooltip enterDelay={1800} title="Create and reprice the quote for the selected recipient."><span><Button type="submit" variant="contained" disabled={!canSubmit || copy.isLoading}>Copy Quote</Button></span></Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default CopyQuotePage;
